import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Download, X, Lock, LogIn, UserPlus, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";

interface Book {
  id: string;
  title: string;
  author: string | null;
  description: string | null;
  summary: string | null;
  cover_url: string | null;
  pdf_url: string;
  page_count: number | null;
}

const Biblioteca = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [readerOpen, setReaderOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loadingBook, setLoadingBook] = useState<string | null>(null);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const { data, error } = await supabase
        .from("library_books")
        .select("id, title, author, description, summary, cover_url, pdf_url, page_count")
        .eq("is_published", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setBooks((data as Book[]) || []);
    } catch (error) {
      console.error("Error fetching books:", error);
      toast.error(t("library.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  const getSignedPdfUrl = async (pdfUrl: string): Promise<string | null> => {
    try {
      // Extract the path from the public URL
      const urlParts = pdfUrl.split("/storage/v1/object/public/library/");
      if (urlParts.length < 2) {
        console.error("Invalid PDF URL format:", pdfUrl);
        return null;
      }
      const filePath = urlParts[1];
      
      // Create a signed URL for authenticated access
      const { data, error } = await supabase.storage
        .from("library")
        .createSignedUrl(filePath, 3600); // 1 hour expiry
      
      if (error) {
        console.error("Error creating signed URL:", error);
        return null;
      }
      
      return data.signedUrl;
    } catch (error) {
      console.error("Error getting signed URL:", error);
      return null;
    }
  };

  const handleReadOnline = async (book: Book) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    setLoadingBook(book.id);
    const signedUrl = await getSignedPdfUrl(book.pdf_url);
    setLoadingBook(null);
    
    if (signedUrl) {
      setSelectedBook({ ...book, pdf_url: signedUrl });
      setReaderOpen(true);
    } else {
      toast.error("Erro ao carregar o livro. Tente novamente.");
    }
  };

  const handleDownload = async (book: Book) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    setLoadingBook(book.id);
    const signedUrl = await getSignedPdfUrl(book.pdf_url);
    setLoadingBook(null);
    
    if (signedUrl) {
      // Use programmatic download for better mobile support
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = `${book.title}.pdf`;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.error("Erro ao baixar o livro. Tente novamente.");
    }
  };

  const handleLogin = () => {
    navigate("/auth?redirect=/biblioteca");
  };

  const handleSignup = () => {
    navigate("/auth?tab=signup&redirect=/biblioteca");
  };

  const defaultCover = "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=300&h=400&fit=crop";

  return (
    <Layout>
      <Helmet>
        <title>{t("library.pageTitle")} | TUPJA</title>
        <meta name="description" content={t("library.metaDescription")} />
      </Helmet>

      {/* Header */}
      <section className="relative py-20 bg-gradient-to-b from-logo-brown/20 to-background">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            {t("library.title")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("library.subtitle")}
          </p>
        </div>
      </section>

      {/* Books Grid */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="flex flex-col items-center">
                  <Skeleton className="w-40 h-56 rounded-r-lg rounded-l-sm" />
                  <Skeleton className="h-4 w-32 mt-3" />
                  <Skeleton className="h-3 w-24 mt-1" />
                </div>
              ))}
            </div>
          ) : books.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-xl text-muted-foreground">{t("library.noBooks")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
              {books.map((book) => (
                <div 
                  key={book.id} 
                  className="group flex flex-col items-center"
                >
                  {/* Book with 3D effect */}
                  <div 
                    className="relative cursor-pointer"
                    style={{ perspective: "1000px" }}
                    onClick={() => handleReadOnline(book)}
                  >
                    {/* Book container with 3D transform */}
                    <div 
                      className="relative transition-all duration-500 ease-out group-hover:scale-105"
                      style={{
                        transformStyle: "preserve-3d",
                        transform: "rotateY(-5deg)",
                      }}
                    >
                      {/* Book spine (left edge) */}
                      <div 
                        className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-amber-900 via-amber-800 to-amber-700 rounded-l-sm"
                        style={{
                          transform: "rotateY(-90deg) translateX(-6px)",
                          transformOrigin: "left",
                        }}
                      />
                      
                      {/* Main book cover */}
                      <div 
                        className="relative w-40 h-56 rounded-r-lg rounded-l-sm overflow-hidden bg-gradient-to-br from-card to-muted"
                        style={{
                          boxShadow: `
                            -8px 8px 20px rgba(0,0,0,0.4),
                            -2px 2px 5px rgba(0,0,0,0.2),
                            inset 0 0 30px rgba(0,0,0,0.1)
                          `,
                        }}
                      >
                        <img
                          src={book.cover_url || defaultCover}
                          alt={book.title}
                          className="w-full h-full object-contain bg-muted/50"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = defaultCover;
                          }}
                        />
                        
                        {/* Glossy overlay effect */}
                        <div 
                          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          style={{
                            background: "linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)",
                          }}
                        />
                        
                        {/* Action buttons - always visible on mobile, hover on desktop */}
                        <div className="absolute inset-0 bg-black/60 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-center gap-2 p-3">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReadOnline(book);
                            }}
                            disabled={loadingBook === book.id}
                            className="w-full gap-1 text-xs"
                          >
                            {loadingBook === book.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                {!user && <Lock className="w-3 h-3" />}
                                <BookOpen className="w-3 h-3" />
                              </>
                            )}
                            {t("library.readOnline")}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(book);
                            }}
                            disabled={loadingBook === book.id}
                            className="w-full gap-1 text-xs bg-background/90 hover:bg-background"
                          >
                            {loadingBook === book.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <>
                                {!user && <Lock className="w-3 h-3" />}
                                <Download className="w-3 h-3" />
                              </>
                            )}
                            {t("library.download")}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Page edges (right side) */}
                      <div 
                        className="absolute right-0 top-1 bottom-1 w-1 bg-gradient-to-r from-stone-100 to-stone-200 rounded-r-sm"
                        style={{
                          boxShadow: "inset -1px 0 2px rgba(0,0,0,0.1)",
                        }}
                      />
                    </div>
                    
                    {/* Shadow on the "shelf" */}
                    <div 
                      className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-36 h-4 bg-black/20 blur-md rounded-full transition-all duration-500 group-hover:w-40 group-hover:bg-black/30 group-hover:-bottom-4"
                    />
                  </div>
                  
                  {/* Book info below */}
                  <div className="mt-4 text-center max-w-40">
                    <h3 className="font-semibold text-foreground text-sm line-clamp-2 group-hover:text-primary transition-colors">
                      {book.title}
                    </h3>
                    {book.author && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                        {book.author}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PDF Reader Modal - Optimized for mobile */}
      <Dialog open={readerOpen} onOpenChange={setReaderOpen}>
        <DialogContent className="max-w-6xl w-[95vw] md:w-full h-[95vh] md:h-[90vh] p-0">
          <DialogHeader className="p-3 md:p-4 border-b flex flex-row items-center justify-between gap-2">
            <DialogTitle className="text-sm md:text-lg font-semibold line-clamp-1 flex-1">
              {selectedBook?.title}
            </DialogTitle>
            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => selectedBook && handleDownload(selectedBook)}
                className="gap-1 text-xs md:text-sm"
              >
                <Download className="w-3 h-3 md:w-4 md:h-4" />
                <span className="hidden sm:inline">{t("library.download")}</span>
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setReaderOpen(false)}
                className="h-8 w-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </DialogHeader>
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            {selectedBook && (
              <>
                <iframe
                  src={`${selectedBook.pdf_url}#toolbar=1&navpanes=0&view=FitH`}
                  className="w-full flex-1 min-h-0"
                  style={{ height: 'calc(95vh - 70px)' }}
                  title={selectedBook.title}
                />
                {/* Fallback for mobile browsers that don't support iframe PDF */}
                <div className="p-3 bg-muted/50 border-t text-center md:hidden">
                  <p className="text-xs text-muted-foreground mb-2">
                    Problema para visualizar? Abra diretamente:
                  </p>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => window.open(selectedBook.pdf_url, '_blank')}
                    className="gap-1 text-xs"
                  >
                    <BookOpen className="w-3 h-3" />
                    Abrir PDF no navegador
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Auth Required Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <DialogTitle className="text-2xl font-display">
              {t("library.authRequired.title")}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {t("library.authRequired.subtitle")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 my-4">
            <div className="flex items-center gap-3 text-sm text-foreground">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>{t("library.authRequired.benefit1")}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-foreground">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>{t("library.authRequired.benefit2")}</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-foreground">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span>{t("library.authRequired.benefit3")}</span>
            </div>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleSignup} 
              className="w-full gap-2"
              size="lg"
            >
              <UserPlus className="w-4 h-4" />
              {t("library.authRequired.signupButton")}
            </Button>
            <Button 
              onClick={handleLogin} 
              variant="outline" 
              className="w-full gap-2"
              size="lg"
            >
              <LogIn className="w-4 h-4" />
              {t("library.authRequired.loginButton")}
            </Button>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-2">
            {t("library.authRequired.freeNote")}
          </p>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Biblioteca;
